import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  modalityTerms,
  policyholders,
  type ModalityTermTag,
  type ParticularClause,
  type ParticularClauseInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate, formatDocument } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { Modal } from '@/shared/ui/Modal';
import { RichTextEditor, type RichTextEditorHandle } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { GradeDeTags, IncluirTag } from './TextoDaModalidade';
import { campoRecusado } from './textos';

/** Caixa de marcacao com rotulo e ajuda (i) do dicionario. */
export function Marcacao({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (marcado: boolean) => void;
  disabled: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 accent-[var(--color-brand-500)]"
      />
      <FieldLabel htmlFor={id} label={label} className="text-sm text-ink-800" />
    </div>
  );
}

function FormularioDaClausula({
  modalityId,
  clausula,
  onFechar,
  onSalva,
}: {
  modalityId: string;
  clausula: ParticularClause | null;
  onFechar: () => void;
  onSalva: (clausula: ParticularClause) => void;
}) {
  const editor = useRef<RichTextEditorHandle>(null);
  const [nome, setNome] = useState(clausula?.name ?? '');
  const [corpo, setCorpo] = useState(clausula?.body ?? '');
  const [corretor, setCorretor] = useState(clausula?.broker_can_select ?? false);
  const [subscricao, setSubscricao] = useState(
    clausula?.automatic_on_underwriting ?? false,
  );
  const [naCotacao, setNaCotacao] = useState(clausula?.enabled_on_quote ?? true);
  const [emBranco, setEmBranco] = useState(clausula?.free_text ?? false);
  const [agregadora, setAgregadora] = useState(clausula?.aggregator_clause ?? '');
  const [tomador, setTomador] = useState(clausula?.policyholder_id ?? '');
  const [tags, setTags] = useState<ModalityTermTag[]>(clausula?.tags ?? []);

  const tomadores = useQuery({
    queryKey: ['policyholders', 'clausula'],
    queryFn: ({ signal }) => policyholders.list({ limit: 100 }, signal),
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: () => {
      const corpoDoPedido: ParticularClauseInput = {
        name: nome.trim(),
        body: corpo,
        broker_can_select: corretor,
        automatic_on_underwriting: subscricao,
        enabled_on_quote: naCotacao,
        free_text: emBranco,
        aggregator_clause: agregadora.trim(),
        policyholder_id: tomador || null,
        tags,
      };
      return clausula
        ? modalityTerms.updateClause(modalityId, clausula.id, corpoDoPedido)
        : modalityTerms.createClause(modalityId, corpoDoPedido);
    },
    onSuccess: onSalva,
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const campoDoErro = campoRecusado(apiError);

  const opcoesDeTomador = [
    { value: '', label: 'Todos os tomadores' },
    ...(tomadores.data?.items ?? []).map((t) => ({
      value: t.id,
      label: `${t.legal_name} (${formatDocument(t.document)})`,
    })),
  ];
  if (tomador && !opcoesDeTomador.some((o) => o.value === tomador)) {
    opcoesDeTomador.push({ value: tomador, label: 'Tomador gravado' });
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <Modal
      open
      size="lg"
      onClose={onFechar}
      title={clausula ? `Editar condicao: ${clausula.name}` : 'Adicionar Condicao'}
      description="O texto e saneado no servidor. As marcacoes de corretor e de subscricao sao guardadas e nao decidem nada ate a homologacao (SUB-014)."
    >
      <form className="flex flex-col gap-4" onSubmit={enviar} noValidate>
        {salvar.error &&
        !['name', 'body', 'policyholder_id'].includes(campoDoErro) &&
        !campoDoErro.startsWith('tags') ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <Field
          label="Condicao"
          required
          value={nome}
          maxLength={200}
          error={fieldErrorOf(apiError, 'name')}
          onChange={(e) => setNome(e.target.value)}
        />
        <GradeDeTags tags={tags} onChange={setTags} grupo="clausula" disabled={false} />
        {campoDoErro.startsWith('tags') ? (
          <p className="text-2xs font-medium text-danger-700">{apiError?.message}</p>
        ) : null}
        <IncluirTag
          declaradas={tags}
          disabled={false}
          onIncluir={(marcacao) => editor.current?.inserirTexto(marcacao)}
        />
        <RichTextEditor
          label="Texto da condicao"
          value={corpo}
          onChange={setCorpo}
          maxLength={clausula?.max_characters ?? 50_000}
          error={fieldErrorOf(apiError, 'body')}
          help="Texto da clausula, com as TAGs. Saneado de novo no servidor ao salvar. A mudanca do texto, do nome ou das TAGs sobe a versao."
          editorRef={editor}
          rows={8}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Marcacao
            label="Corretor pode inserir?"
            checked={corretor}
            onChange={setCorretor}
            disabled={false}
          />
          <Marcacao
            label="Automatica Subscricao?"
            checked={subscricao}
            onChange={setSubscricao}
            disabled={false}
          />
          <Marcacao
            label="Habilitar na cotacao"
            checked={naCotacao}
            onChange={setNaCotacao}
            disabled={false}
          />
          <Marcacao
            label="Clausula em branco"
            checked={emBranco}
            onChange={setEmBranco}
            disabled={false}
          />
        </div>
        <Field
          label="Clausula agregadora"
          value={agregadora}
          maxLength={2000}
          error={fieldErrorOf(apiError, 'aggregator_clause')}
          onChange={(e) => setAgregadora(e.target.value)}
        />
        <Select
          label="Tomador da clausula"
          options={opcoesDeTomador}
          value={tomador}
          error={fieldErrorOf(apiError, 'policyholder_id')}
          hint={
            tomadores.isError
              ? 'A lista de tomadores nao abriu; a clausula vale para todos.'
              : undefined
          }
          onChange={(e) => setTomador(e.target.value)}
        />
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Fechar
          </Button>
          <Button type="submit" loading={salvar.isPending}>
            Salvar condicao
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Aba "Condicoes Particulares": Condicao, Data de cadastro, Status e Acoes (Editar e
 * Desativar), com "Adicionar Condicao". A aba mostra tambem as inativas, como o
 * OnPoint (Core/GetParticularDetailClauses).
 */
export function CondicoesParticulares({ modalityId }: { modalityId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEscrever = can('parametrizacao.texto.update');
  const [editando, setEditando] = useState<ParticularClause | 'nova' | null>(null);
  const chave = ['particular-clauses', modalityId];

  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) =>
      modalityTerms.listClauses(modalityId, { include_inactive: true }, signal),
  });
  const situacao = useMutation({
    mutationFn: (clausula: ParticularClause) =>
      modalityTerms.updateClause(modalityId, clausula.id, {
        status: clausula.status === 'ativo' ? 'inativo' : 'ativo',
      }),
    onSuccess: (clausula) => {
      toast.success(
        clausula.status === 'ativo' ? 'Condicao ativada.' : 'Condicao desativada.',
      );
      void queryClient.invalidateQueries({ queryKey: chave });
    },
    onError: (erro) => toast.error(erro.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-600">
          As condicoes particulares que a cotacao pode incluir nesta modalidade.
        </p>
        {podeEscrever ? (
          <Button size="sm" onClick={() => setEditando('nova')}>
            Adicionar Condicao
          </Button>
        ) : null}
      </div>
      {consulta.error ? (
        <Alert tone="danger" title="Nao foi possivel carregar">
          {consulta.error.message}
        </Alert>
      ) : (
        <DataTable
          loading={consulta.isPending}
          columns={[
            {
              key: 'condicao',
              header: 'Condicao',
              render: (c: ParticularClause) => (
                <div className="flex flex-col">
                  <span className="font-medium text-ink-900">{c.name}</span>
                  <span className="text-2xs text-ink-500">
                    Versao {c.version}
                    {c.free_text ? ' . em branco' : ''}
                    {c.policyholder_id ? ' . de um tomador' : ''}
                  </span>
                </div>
              ),
            },
            {
              key: 'cadastro',
              header: 'Data de cadastro',
              render: (c: ParticularClause) => formatDate(c.created_at),
            },
            {
              key: 'status',
              header: 'Status',
              render: (c: ParticularClause) => (
                <StatusBadge kind="cadastro" value={c.status} />
              ),
            },
            {
              key: 'acoes',
              header: 'Acoes',
              render: (c: ParticularClause) =>
                podeEscrever ? (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditando(c)}>
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={situacao.isPending && situacao.variables?.id === c.id}
                      onClick={() => situacao.mutate(c)}
                    >
                      {c.status === 'ativo' ? 'Desativar' : 'Ativar'}
                    </Button>
                  </div>
                ) : (
                  '-'
                ),
            },
          ]}
          items={consulta.data ?? []}
          keyOf={(c) => c.id}
          empty={<EmptyState icon="file" title="Nenhuma condicao particular ainda" />}
        />
      )}
      {editando ? (
        <FormularioDaClausula
          modalityId={modalityId}
          clausula={editando === 'nova' ? null : editando}
          onFechar={() => setEditando(null)}
          onSalva={(clausula) => {
            setEditando(null);
            toast.success(`Condicao salva na versao ${clausula.version}.`);
            void queryClient.invalidateQueries({ queryKey: chave });
          }}
        />
      ) : null}
    </div>
  );
}
